// v3 pure engine: use case → formula steps, sub-totals, value, leading-metric values; plus the
// rollup (gross → program → Clay-attributed → ROI) and the helpers the UI reads.
//
// Alongside the v2 engine (engine/engine.ts) until the UI switches over; nothing here imports it.
// Retired from v2 and deliberately absent: scenario, revenue focus, time mode, custom levers, the
// profile-level attribution seed (and its 10% global fallback).
//
// Conventions
//  - Percent answers are whole percent numbers (82 = 82%). Step values use the same units.
//  - A cleared answer ('') resolves to 0; a missing answer resolves to the question default. A blank
//    realization / attribution resolves to the use case default — blank never means zero.
//  - A "with Clay − today" difference is floored at 0: Clay is never modelled as removing value.
//  - A pipeline-path use case's value is revenue (pipeline × win rate); pipeline is a sub-total.
//  - Overlap never discounts: both record levels add, declared pairs only produce a note.

import { DEFAULT_TOOLS16, TOOLS16 } from '../data/tools';
import {
  SIZED_USE_CASES,
  getSizedUseCase,
  type LeadingMetric,
  type MetricSource,
  type RecordLevel,
  type SizedUseCase,
} from '../data/use-case-catalog';
import type { CustomTool } from '../types';

/* ---------------------------------------------------------------------------- types */

export type StepUnit = 'count' | 'pct' | 'money' | 'hours';
export type StepOperator = '×' | '−' | '=';

/** One formula card. `operator` joins it to the previous card; the first card has none. */
export interface FormulaStep {
  label: string;
  description: string;
  operator: StepOperator | null;
  value: number;
  unit: StepUnit;
  /** Set on '=' cards that are named sub-totals (opportunities, pipeline, revenue, …). */
  subTotal: string | null;
}

export interface SubTotal {
  key: string;
  label: string;
  value: number;
  unit: StepUnit;
}

/** One formula chain. A use case has one section, or one per enabled record level. */
export interface FormulaSection {
  level: RecordLevel | null;
  steps: FormulaStep[];
  subTotals: SubTotal[];
  value: number;
  /** Inbound only: the today vs with-Clay chains shown side by side above the difference. */
  columns?: { today: FormulaStep[]; withClay: FormulaStep[] };
}

export interface MetricValue {
  key: string;
  name: string;
  unit: string;
  measurement: LeadingMetric['measurement'];
  recordLevel: RecordLevel | null;
  today: number;
  /** null when the metric has no today / with-Clay split. */
  withClay: number | null;
  /** The today value came from a default rather than something the customer gave. */
  assumed: boolean;
}

export interface UseCaseResult {
  id: string;
  recordLevels: RecordLevel[];
  /** Calculation methods in force; set only for use cases that offer methods (GTM automation). */
  methods?: string[];
  sections: FormulaSection[];
  /** Gross value, before realization: revenue, or cost saved. */
  value: number;
  /** Σ of the 'pipeline' sub-totals; 0 for use cases that do not create pipeline. */
  pipeline: number;
  metrics: MetricValue[];
  notes: string[];
  /** Tool consolidation only: the displaced tools behind the number (restored on load). */
  displacedTools: ToolRow[] | null;
  /** Deal-level inputs the formula needs but did not get (the value is 0 until they arrive). */
  missingInputs: string[];
}

export interface DealEconomics {
  /** Average deal size, dollars. */
  acv: number;
  /** Win rate, whole percent. */
  winRate: number;
  /** The annual Clay cost ROI divides by. Blank (null) by default — never pre-filled. */
  contractEstimate: number | null;
  /** Required only when a selected use case reads it (Account / lead scoring). */
  currentAnnualPipeline: number | null;
}

export type UseCaseAnswers = Record<string, number | ''>;

export interface UseCaseSelection {
  id: string;
  /** Defaults to the use case's first offered level (Accounts). Ignored if it offers none. */
  recordLevels?: RecordLevel[];
  /** Calculation methods that are on; absent / empty = the first one only (GTM automation). */
  methods?: string[];
  answers?: UseCaseAnswers;
  /** Blank / absent restores the use case default. */
  realization?: number | '' | null;
  attribution?: number | '' | null;
}

export interface CalculatorInput {
  deal: DealEconomics;
  selected: UseCaseSelection[];
  /**
   * Tool consolidation checklist: a present key is a displaced tool at that contract value.
   * Absent / null means untouched: the default checklist (ZoomInfo) applies and is flagged Assumed.
   */
  tools?: Record<string, number | ''> | null;
  customTools?: CustomTool[] | null;
}

export interface RollupLine {
  id: string;
  value: number;
  realizationPct: number;
  programValue: number;
  attributionPct: number;
  attributedValue: number;
  /** Gross pipeline created (0 for use cases outside the pipeline path). */
  pipeline: number;
}

export interface Rollup {
  lines: RollupLine[];
  gross: number;
  program: number;
  attributed: number;
  /** Gross pipeline created, before realization. */
  pipelineGross: number;
  /** Pipeline created on a program basis (each use case's pipeline × its realization). */
  pipeline: number;
  /** attributed ÷ contract estimate; null when the estimate is blank or 0. */
  roi: number | null;
  missingInputs: string[];
}

export interface CalculatorResult {
  useCases: UseCaseResult[];
  rollup: Rollup;
}

export const DEFAULT_DEAL: DealEconomics = {
  // Mirrors the catalog constants; kept literal so a typo in one place fails the golden fixtures.
  acv: 40000,
  winRate: 25,
  contractEstimate: null,
  currentAnnualPipeline: 80_000_000,
};

/* ------------------------------------------------------------------- tool checklist */

export interface ToolRow {
  /** TOOLS16 id, or null for a custom tool. */
  id: string | null;
  name: string;
  spend: number;
}

export function toolRows(
  tools: Record<string, number | ''> | null | undefined,
  customTools: CustomTool[] | null | undefined,
): ToolRow[] {
  const t = tools ?? {};
  const rows: ToolRow[] = TOOLS16.filter((x) => t[x.id] != null).map((x) => ({
    id: x.id,
    name: x.name,
    spend: Number(t[x.id]) || 0,
  }));
  for (const c of customTools ?? []) {
    if ((c.name || '').trim() || Number(c.spend) > 0)
      rows.push({ id: null, name: (c.name || '').trim() || 'Custom tool', spend: Number(c.spend) || 0 });
  }
  return rows;
}

/* ---------------------------------------------------------------- answer resolution */

/** The record levels a selection actually has on, normalised against what the use case offers. */
export function enabledLevels(uc: SizedUseCase, requested?: RecordLevel[] | null): RecordLevel[] {
  if (uc.recordLevels.length === 0) return [];
  const on = uc.recordLevels.filter((l) => (requested ?? []).includes(l));
  return on.length ? on : [uc.recordLevels[0]];
}

/** The calculation methods a selection actually has on: catalog order, never empty (the first is the fallback). */
export function enabledMethods(uc: SizedUseCase, requested?: readonly string[] | null): string[] {
  if (uc.methods.length === 0) return [];
  const on = uc.methods.map((m) => m.id).filter((id) => (requested ?? []).includes(id));
  return on.length ? on : [uc.methods[0].id];
}

function questionsFor(uc: SizedUseCase, levels: RecordLevel[], methods: readonly string[]) {
  return uc.questions.filter(
    (q) => (!q.recordLevel || levels.includes(q.recordLevel)) && (!q.method || methods.includes(q.method)),
  );
}

/** answer ?? default for every question that is asked at the enabled levels. */
export function resolveUseCaseAnswers(
  uc: SizedUseCase,
  answers: UseCaseAnswers | null | undefined,
  levels: RecordLevel[],
  dealAcv: number,
  /** Methods that are on; omitted = the use case's first method only. */
  methods?: readonly string[] | null,
): Record<string, number> {
  const saved = answers ?? {};
  const out: Record<string, number> = {};
  for (const q of questionsFor(uc, levels, enabledMethods(uc, methods))) {
    const sv = saved[q.key];
    out[q.key] = sv !== undefined ? (sv === '' ? 0 : Number(sv) || 0) : q.defaultFromDealAcv ? dealAcv : q.def;
  }
  return out;
}

/** A realization / attribution entry resolved against the use case default (blank = default, never zero). */
export function resolveFactor(v: number | '' | null | undefined, def: number): number {
  if (v === '' || v == null) return def;
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}

/* -------------------------------------------------------------------- step builder */

interface Chain {
  steps: FormulaStep[];
  /** × a rate or factor. */
  x(label: string, description: string, value: number, unit: StepUnit): number;
  /** − a quantity. */
  minus(label: string, description: string, value: number, unit: StepUnit): number;
  /** The first card, or a card with no operator. */
  start(label: string, description: string, value: number, unit: StepUnit): number;
  /** = a result; `key` names it as a sub-total. */
  eq(label: string, description: string, value: number, unit: StepUnit, key: string): number;
}

function chain(): Chain {
  const steps: FormulaStep[] = [];
  const add =
    (operator: StepOperator | null) =>
    (label: string, description: string, value: number, unit: StepUnit, subTotal: string | null = null) => {
      steps.push({ label, description, operator, value, unit, subTotal });
      return value;
    };
  const start = add(null);
  const x = add('×');
  const minus = add('−');
  const eq = add('=');
  return {
    steps,
    start: (l, d, v, u) => start(l, d, v, u),
    x: (l, d, v, u) => x(l, d, v, u),
    minus: (l, d, v, u) => minus(l, d, v, u),
    eq: (l, d, v, u, key) => eq(l, d, v, u, key),
  };
}

function section(
  level: RecordLevel | null,
  ch: Chain,
  value: number,
  columns?: FormulaSection['columns'],
): FormulaSection {
  const subTotals: SubTotal[] = ch.steps
    .filter((s) => s.subTotal)
    .map((s) => ({ key: s.subTotal as string, label: s.label, value: s.value, unit: s.unit }));
  return { level, steps: ch.steps, subTotals, value, ...(columns ? { columns } : {}) };
}

const n0 = (v: number) => (Math.round(v * 100) / 100).toLocaleString('en-US');
const usd = (v: number) => '$' + Math.round(v).toLocaleString('en-US');
const diff = (clay: number, today: number) => Math.max(0, clay - today);
/** A rate difference with float noise (3.9 − 3 = 0.8999999999999999) rounded away. */
const rateDiff = (clay: number, today: number) => Math.round(diff(clay, today) * 1e9) / 1e9;
/** Win-rate increase in points: today's win rate × the relative lift, shown as today → with Clay. */
function winRateIncrease(ch: Chain, winPct: number, liftPct: number): number {
  const inc = (winPct * liftPct) / 100;
  ch.x('Win-rate increase', `${n0(winPct)}% today → ${n0(winPct + inc)}% with Clay`, inc, 'pct');
  return inc;
}

interface Ctx {
  a: Record<string, number>;
  acv: number;
  winPct: number;
  currentAnnualPipeline: number | null;
  tools: ToolRow[];
}

/** × ACV = pipeline × win rate = revenue. Returns revenue. */
function pipelineTail(ch: Chain, opps: number, acv: number, winPct: number, acvLabel = 'Average deal size'): number {
  ch.x(acvLabel, 'Average contract value of a won deal', acv, 'money');
  const pipeline = ch.eq('Pipeline', 'Opportunities × average deal size', opps * acv, 'money', 'pipeline');
  ch.x('Win rate', 'Share of pipeline that closes', winPct, 'pct');
  return ch.eq('Revenue', 'Pipeline × win rate', (pipeline * winPct) / 100, 'money', 'revenue');
}

/* ---------------------------------------------------------------------- formulas */

type Formula = (c: Ctx, levels: RecordLevel[], methods: string[]) => FormulaSection[];

const FORMULAS: Record<string, Formula> = {
  // Q26 / Q32: only baseline records count; contacts add a share-worked haircut.
  'uc-crm-enrichment': (c, levels) => {
    const { a } = c;
    const out: FormulaSection[] = [];
    if (levels.includes('accounts')) {
      const ch = chain();
      const records = ch.start('Account records', 'Account records in your CRM', a.accounts_records, 'count');
      const d = diff(a.accounts_fill_clay, a.accounts_fill_current);
      ch.x('Change in fill rate', `${n0(a.accounts_fill_clay)}% with Clay − ${n0(a.accounts_fill_current)}% today`, d, 'pct');
      const actionable = ch.eq('Records made actionable', 'Records × change in fill rate', (records * d) / 100, 'count', 'records');
      ch.x('Records-to-opportunity rate', 'Newly actionable records that become opportunities', a.accounts_record_to_opp, 'pct');
      const opps = ch.eq('Opportunities', 'Actionable records × records-to-opportunity rate', (actionable * a.accounts_record_to_opp) / 100, 'count', 'opportunities');
      out.push(section('accounts', ch, pipelineTail(ch, opps, c.acv, c.winPct)));
    }
    if (levels.includes('contacts')) {
      const ch = chain();
      const records = ch.start('Contacts', 'Contact records in your CRM', a.contacts_records, 'count');
      const d = diff(a.contacts_reachable_clay, a.contacts_reachable_current);
      ch.x('Change in contactable rate', `${n0(a.contacts_reachable_clay)}% with Clay − ${n0(a.contacts_reachable_current)}% today`, d, 'pct');
      const reachable = ch.eq('Contacts made reachable', 'Contacts × change in contactable rate', (records * d) / 100, 'count', 'records');
      ch.x('Share worked', 'Newly reachable contacts that your reps actually work', a.contacts_share_worked, 'pct');
      const worked = ch.eq('Contacts worked', 'Reachable contacts × share worked', (reachable * a.contacts_share_worked) / 100, 'count', 'worked');
      ch.x('Contact-to-meeting rate', 'Worked contacts that become meetings', a.contacts_to_meeting, 'pct');
      const meetings = ch.eq('Meetings', 'Contacts worked × contact-to-meeting rate', (worked * a.contacts_to_meeting) / 100, 'count', 'meetings');
      ch.x('Meeting-to-opportunity rate', 'Meetings that become opportunities', a.contacts_meeting_to_opp, 'pct');
      const opps = ch.eq('Opportunities', 'Meetings × meeting-to-opportunity rate', (meetings * a.contacts_meeting_to_opp) / 100, 'count', 'opportunities');
      out.push(section('contacts', ch, pipelineTail(ch, opps, c.acv, c.winPct)));
    }
    return out;
  },

  // Q27: win-rate path. Reads current annual pipeline (deal economics); 0 until it is given.
  'uc-account-lead-scoring': (c) => {
    const { a } = c;
    const ch = chain();
    const pipe = ch.start('Current annual pipeline', 'Pipeline your team works in a year', c.currentAnnualPipeline ?? 0, 'money');
    const d = diff(a.coverage_clay, a.coverage_current);
    ch.x('Change in scoring coverage', `${n0(a.coverage_clay)}% with Clay − ${n0(a.coverage_current)}% today`, d, 'pct');
    const scored = ch.eq('Pipeline newly scored', 'Pipeline × change in scoring coverage', (pipe * d) / 100, 'money', 'scored_pipeline');
    const inc = winRateIncrease(ch, c.winPct, a.winrate_lift);
    const revenue = ch.eq('Revenue', 'Pipeline newly scored × win-rate increase', (scored * inc) / 100, 'money', 'revenue');
    return [section(null, ch, revenue)];
  },

  // Q28 / Q33: only incremental sourcing counts.
  'uc-tam-sourcing': (c, levels) => {
    const { a } = c;
    const out: FormulaSection[] = [];
    if (levels.includes('accounts')) {
      const ch = chain();
      const clay = ch.start('Accounts sourced per year with Clay', 'Net-new ICP accounts sourced with Clay', a.accounts_year_clay, 'count');
      const today = ch.minus('Accounts sourced per year today', 'Net-new ICP accounts sourced today', a.accounts_year_today, 'count');
      const net = ch.eq('Net-new accounts', 'With Clay − today', diff(clay, today), 'count', 'records');
      ch.x('Account-to-opportunity rate', 'Sourced accounts that become opportunities', a.accounts_to_opp, 'pct');
      const opps = ch.eq('Opportunities', 'Net-new accounts × account-to-opportunity rate', (net * a.accounts_to_opp) / 100, 'count', 'opportunities');
      out.push(section('accounts', ch, pipelineTail(ch, opps, c.acv, c.winPct)));
    }
    if (levels.includes('contacts')) {
      const ch = chain();
      const clay = ch.start('Contacts sourced per year with Clay', 'Net-new ICP contacts sourced with Clay', a.contacts_year_clay, 'count');
      const today = ch.minus('Contacts sourced per year today', 'Net-new ICP contacts sourced today', a.contacts_year_today, 'count');
      const net = ch.eq('Net-new contacts', 'With Clay − today', diff(clay, today), 'count', 'records');
      ch.x('Contact-to-meeting rate', 'Sourced contacts that become meetings', a.contacts_to_meeting, 'pct');
      const meetings = ch.eq('Meetings', 'Net-new contacts × contact-to-meeting rate', (net * a.contacts_to_meeting) / 100, 'count', 'meetings');
      ch.x('Meeting-to-opportunity rate', 'Meetings that become opportunities', a.contacts_meeting_to_opp, 'pct');
      const opps = ch.eq('Opportunities', 'Meetings × meeting-to-opportunity rate', (meetings * a.contacts_meeting_to_opp) / 100, 'count', 'opportunities');
      out.push(section('contacts', ch, pipelineTail(ch, opps, c.acv, c.winPct)));
    }
    return out;
  },

  // Q44: today vs with-Clay on the same base. Unmatched leads convert at 0, so they add nothing.
  'uc-inbound-speed-to-lead': (c) => {
    const { a } = c;
    const lifted = (a.conversion_current * (100 + a.speed_lift)) / 100;
    const column = (label: string, match: number, conversion: number) => {
      const ch = chain();
      const leads = ch.start('Inbound leads per year', 'All inbound leads', a.leads_year, 'count');
      ch.x('Qualified', 'Leads that are qualified', a.qualified_pct, 'pct');
      const qualified = (leads * a.qualified_pct) / 100;
      ch.x('Match rate', `Qualified leads matched to a known account (${label})`, match, 'pct');
      const matched = (qualified * match) / 100;
      ch.x('Lead-to-opportunity conversion', `Matched leads that become opportunities (${label})`, conversion, 'pct');
      const opps = ch.eq(`Opportunities ${label}`, 'Leads × qualified × match rate × conversion', (matched * conversion) / 100, 'count', 'opportunities');
      return { steps: ch.steps, opps };
    };
    const today = column('today', a.match_current, a.conversion_current);
    const clay = column('with Clay', a.match_clay, lifted);
    const ch = chain();
    ch.start('Opportunities with Clay', 'From the with-Clay column', clay.opps, 'count');
    ch.minus('Opportunities today', 'From the today column', today.opps, 'count');
    const added = ch.eq('Additional opportunities', 'With Clay − today', diff(clay.opps, today.opps), 'count', 'opportunities');
    return [section(null, ch, pipelineTail(ch, added, c.acv, c.winPct), { today: today.steps, withClay: clay.steps })];
  },

  // Q40: starts from the outreach volume sent through automated outbound (a new motion, so all of it counts).
  'uc-automated-outbound': (c) => {
    const { a } = c;
    const ch = chain();
    const contacts = ch.start('Outreach sent per year', 'Contacts sequenced per year through automated outbound', a.outreach_year, 'count');
    ch.x('Meeting rate', 'Meeting rate on sequenced contacts', a.meeting_rate, 'pct');
    const meetings = ch.eq('Meetings', 'Outreach sent × meeting rate', (contacts * a.meeting_rate) / 100, 'count', 'meetings');
    ch.x('Meeting-to-opportunity rate', 'Meetings that become opportunities', a.meeting_to_opp, 'pct');
    const opps = ch.eq('Opportunities', 'Meetings × meeting-to-opportunity rate', (meetings * a.meeting_to_opp) / 100, 'count', 'opportunities');
    return [section(null, ch, pipelineTail(ch, opps, c.acv, c.winPct))];
  },

  // Q41: cost path — wasted ad spend recovered.
  'uc-performance-marketing': (c) => {
    const { a } = c;
    const ch = chain();
    const spend = ch.start('Annual ad spend', 'Your paid-ad spend per year', a.ad_spend_year, 'money');
    const d = diff(a.match_clay, a.match_current);
    ch.x('Change in audience match rate', `${n0(a.match_clay)}% with Clay − ${n0(a.match_current)}% today`, d, 'pct');
    const wasted = ch.eq('Spend no longer wasted', 'Ad spend × change in match rate', (spend * d) / 100, 'money', 'spend_reached');
    ch.x('Recoverable share', 'Share of that wasted spend that is recoverable', a.recoverable_pct, 'pct');
    const value = ch.eq('Wasted spend recovered', 'Spend no longer wasted × recoverable share', (wasted * a.recoverable_pct) / 100, 'money', 'cost_saved');
    return [section(null, ch, value)];
  },

  // Q42: no rep-hours value here — labor dollars live only in GTM automation.
  'uc-rep-prospecting': (c) => {
    const { a } = c;
    const ch = chain();
    const clay = ch.start('Prospects per rep per week with Clay', 'Prospects added per rep with Clay', a.prospects_week_clay, 'count');
    const today = ch.minus('Prospects per rep per week today', 'Prospects added per rep today', a.prospects_week_today, 'count');
    const perRep = ch.eq('Added prospects per rep per week', 'With Clay − today', diff(clay, today), 'count', 'added_per_rep');
    ch.x('Reps', 'Reps prospecting on your team', a.reps, 'count');
    ch.x('Working weeks', 'Working weeks per year', 48, 'count');
    const prospects = ch.eq('Added prospects per year', 'Added prospects per rep × reps × 48', perRep * a.reps * 48, 'count', 'prospects');
    ch.x('Meeting rate', 'Meeting rate on prospects', a.meeting_rate, 'pct');
    const meetings = ch.eq('Meetings', 'Added prospects × meeting rate', (prospects * a.meeting_rate) / 100, 'count', 'meetings');
    ch.x('Meeting-to-opportunity rate', 'Meetings that become opportunities', a.meeting_to_opp, 'pct');
    const opps = ch.eq('Opportunities', 'Meetings × meeting-to-opportunity rate', (meetings * a.meeting_to_opp) / 100, 'count', 'opportunities');
    return [section(null, ch, pipelineTail(ch, opps, c.acv, c.winPct))];
  },

  // Q46: the value is the rate gain from timing.
  'uc-signal-based-outbound': (c) => {
    const { a } = c;
    const ch = chain();
    const month = ch.start('Signal-triggered contacts per month', 'Contacts that fire a buying signal each month', a.signal_contacts_month, 'count');
    ch.x('Months', 'Months per year', 12, 'count');
    const contacts = ch.eq('Signal-triggered contacts per year', 'Contacts per month × 12', month * 12, 'count', 'contacts');
    const d = rateDiff(a.meeting_rate_clay, a.meeting_rate_today);
    ch.x('Change in meeting rate', `${n0(a.meeting_rate_clay)}% with Clay − ${n0(a.meeting_rate_today)}% today`, d, 'pct');
    const added = ch.eq('Added meetings', 'Contacts × change in meeting rate', (contacts * d) / 100, 'count', 'meetings');
    ch.x('Meeting-to-opportunity rate', 'Meetings that become opportunities', a.meeting_to_opp, 'pct');
    const opps = ch.eq('Opportunities', 'Added meetings × meeting-to-opportunity rate', (added * a.meeting_to_opp) / 100, 'count', 'opportunities');
    return [section(null, ch, pipelineTail(ch, opps, c.acv, c.winPct))];
  },

  // Q47: valued at the PLG ACV (own input, defaults to the company ACV).
  'uc-plg-expansion': (c) => {
    const { a } = c;
    const ch = chain();
    const month = ch.start('Signups per month', 'Your product signups per month', a.signups_month, 'count');
    ch.x('Months', 'Months per year', 12, 'count');
    const signups = ch.eq('Signups per year', 'Signups per month × 12', month * 12, 'count', 'signups');
    ch.x('ICP fit', 'Signups that fit the ICP', a.icp_fit, 'pct');
    const icp = ch.eq('ICP-fit signups', 'Signups × ICP fit', (signups * a.icp_fit) / 100, 'count', 'icp_signups');
    const d = rateDiff(a.conversion_clay, a.conversion_today);
    ch.x('Change in conversion', `${n0(a.conversion_clay)}% with Clay − ${n0(a.conversion_today)}% today`, d, 'pct');
    const opps = ch.eq('Added opportunities', 'ICP-fit signups × change in conversion', (icp * d) / 100, 'count', 'opportunities');
    return [section(null, ch, pipelineTail(ch, opps, a.plg_acv, c.winPct, 'PLG average deal size'))];
  },

  // Q49: win-rate path — accounts enrolled is program size, not a delta.
  'uc-abm': (c) => {
    const { a } = c;
    const ch = chain();
    const enrolled = ch.start('Accounts enrolled', 'Accounts in your ABM program', a.accounts_enrolled, 'count');
    ch.x('With an open opportunity', 'Enrolled accounts with an open opportunity each year', a.open_opp_pct, 'pct');
    const opps = ch.eq('Opportunities', 'Accounts enrolled × share with an open opportunity', (enrolled * a.open_opp_pct) / 100, 'count', 'opportunities');
    ch.x('Average deal size', 'Average contract value of a won deal', c.acv, 'money');
    const pipeline = ch.eq('Pipeline on ABM accounts', 'Opportunities × average deal size', opps * c.acv, 'money', 'abm_pipeline');
    const inc = winRateIncrease(ch, c.winPct, a.winrate_uplift);
    const revenue = ch.eq('Revenue', 'ABM pipeline × win-rate increase', (pipeline * inc) / 100, 'money', 'revenue');
    return [section(null, ch, revenue)];
  },

  // Q50: the checked tools' contract values are the tool spend removed.
  'uc-tool-consolidation': (c) => {
    const ch = chain();
    const total = c.tools.reduce((t, r) => t + r.spend, 0);
    ch.start(
      'Displaced tool contracts',
      c.tools.length ? c.tools.map((t) => `${t.name} ${usd(t.spend)}`).join(' + ') : 'No tools selected',
      total,
      'money',
    );
    const saved = ch.eq('Tool spend removed', 'Annual contract value of the displaced tools', total, 'money', 'cost_saved');
    return [section(null, ch, saved)];
  },

  // Q51: the single use case carrying labor dollars. Three ways to size the hours Clay saves; the
  // selected methods' hours add, then the hourly rate applies once. With one method on, its
  // hours-per-year step is the total (sub-total `hours_year`); with two or more each method keeps
  // its own `hours_year_<id>` and a "Total hours saved per year" step sums them into `hours_year`.
  'uc-gtm-automation': (c, _levels, methods) => {
    const { a } = c;
    const ch = chain();
    const multi = methods.length > 1;
    const yearKey = (id: string) => (multi ? `hours_year_${id}` : 'hours_year');
    const years: number[] = [];
    const yearLabels: string[] = [];
    if (methods.includes('share')) {
      const hours = ch.start('Team hours per week', 'Hours per week your team spends on manual GTM tasks', a.hours_week, 'hours');
      ch.x('Automation share', 'Share of that work Clay can automate', a.automation_pct, 'pct');
      const weekly = ch.eq('Automated hours per week', 'Team hours × automation share', (hours * a.automation_pct) / 100, 'hours', 'hours_week');
      ch.x('Weeks', 'Weeks per year', 52, 'count');
      yearLabels.push('Automated hours per year');
      years.push(ch.eq('Automated hours per year', 'Automated hours per week × 52', weekly * 52, 'hours', yearKey('share')));
    }
    if (methods.includes('flat')) {
      const weekly = ch.start('Flat hours saved per week', 'Hours per week Clay saves your team, however you count them', a.hours_saved_week, 'hours');
      ch.x('Weeks', 'Weeks per year', 52, 'count');
      yearLabels.push('Flat hours saved per year');
      years.push(ch.eq('Flat hours saved per year', 'Hours saved per week × 52', weekly * 52, 'hours', yearKey('flat')));
    }
    if (methods.includes('records')) {
      ch.start('Minutes saved per record', 'Minutes Clay saves on each record your team works', a.minutes_per_record, 'count');
      ch.x('Records worked per week', 'Records your team works each week', a.records_week, 'count');
      ch.x('Hours conversion', '÷ 60 minutes per hour × 52 weeks per year', 52 / 60, 'count');
      yearLabels.push('Record-based hours saved per year');
      years.push(
        ch.eq('Record-based hours saved per year', 'Minutes per record × records per week ÷ 60 × 52', ((a.minutes_per_record * a.records_week) / 60) * 52, 'hours', yearKey('records')),
      );
    }
    const total = multi
      ? ch.eq('Total hours saved per year', 'The selected methods’ hours added together', years.reduce((t, v) => t + v, 0), 'hours', 'hours_year')
      : (years[0] ?? 0);
    const rate = a.annual_cost / 2080;
    ch.x('Cost per hour', `${usd(a.annual_cost)} fully-loaded cost ÷ 2,080 hours`, rate, 'money');
    const saved = ch.eq('Labor cost saved', `${multi ? 'Total hours saved per year' : yearLabels[0]} × cost per hour`, total * rate, 'money', 'cost_saved');
    return [section(null, ch, saved)];
  },
};

/* --------------------------------------------------------------------- metric values */

function evalSource(src: MetricSource, a: Record<string, number>, ctx: Ctx, sections: readonly FormulaSection[]): number {
  switch (src.kind) {
    case 'question':
      return a[src.key] ?? 0;
    case 'lifted':
      return (evalSource(src.base, a, ctx, sections) * (100 + (a[src.liftKey] ?? 0))) / 100;
    case 'deal-win-rate':
      return ctx.winPct;
    case 'constant':
      return src.value;
    case 'tools-displaced':
      return ctx.tools.length;
    case 'sub-total':
      return sections.reduce((t, sec) => t + (sec.subTotals.find((x) => x.key === src.key)?.value ?? 0), 0);
  }
}

function sourceAssumed(
  src: MetricSource,
  answers: UseCaseAnswers | null | undefined,
  toolsUntouched: boolean,
): boolean {
  switch (src.kind) {
    case 'question':
      return (answers ?? {})[src.key] === undefined;
    case 'lifted':
      return sourceAssumed(src.base, answers, toolsUntouched);
    case 'deal-win-rate':
      return false;
    case 'constant':
      return true;
    case 'tools-displaced':
      return toolsUntouched;
    case 'sub-total':
      return false;
  }
}

/* ---------------------------------------------------------------------- use case */

const BOTH_LEVELS_NOTE =
  'Both record levels are on, so their values add. Lower realization if the account and contact records overlap.';

/** Compute one selected use case. Pure: same input, same result. */
export function computeUseCase(
  sel: UseCaseSelection,
  deal: DealEconomics,
  toolInput?: { tools?: Record<string, number | ''> | null; customTools?: CustomTool[] | null },
): UseCaseResult | null {
  const uc = getSizedUseCase(sel.id);
  if (!uc) return null;
  const levels = enabledLevels(uc, sel.recordLevels);
  const methods = enabledMethods(uc, sel.methods);
  const a = resolveUseCaseAnswers(uc, sel.answers, levels, deal.acv, methods);
  const toolsUntouched = toolInput?.tools == null && !(toolInput?.customTools ?? []).length;
  const ctx: Ctx = {
    a,
    acv: deal.acv,
    winPct: deal.winRate,
    currentAnnualPipeline: deal.currentAnnualPipeline,
    tools: toolRows(toolInput?.tools ?? DEFAULT_TOOLS16, toolInput?.customTools),
  };
  const missingInputs: string[] = [];
  if (uc.requiresCurrentAnnualPipeline && !(Number(deal.currentAnnualPipeline) > 0))
    missingInputs.push('currentAnnualPipeline');

  const sections = FORMULAS[uc.id](ctx, levels, methods);
  const value = sections.reduce((t, s) => t + s.value, 0);
  const pipeline =
    uc.path === 'pipeline'
      ? sections.reduce((t, s) => t + (s.subTotals.find((x) => x.key === 'pipeline')?.value ?? 0), 0)
      : 0;

  const metrics: MetricValue[] = uc.leadingMetrics
    .filter((m) => (!m.recordLevel || levels.includes(m.recordLevel)) && (!m.method || methods.includes(m.method)))
    .map((m) => ({
      key: m.key,
      name: m.name,
      unit: m.unit,
      measurement: m.measurement,
      recordLevel: m.recordLevel ?? null,
      today: evalSource(m.today, a, ctx, sections),
      withClay: m.withClay ? evalSource(m.withClay, a, ctx, sections) : null,
      assumed: sourceAssumed(m.today, sel.answers, toolsUntouched),
    }));

  return {
    id: uc.id,
    recordLevels: levels,
    ...(methods.length ? { methods } : {}),
    sections,
    value,
    pipeline,
    metrics,
    notes: levels.length > 1 ? [BOTH_LEVELS_NOTE] : [],
    displacedTools: uc.id === 'uc-tool-consolidation' ? ctx.tools : null,
    missingInputs,
  };
}

/** The selection in catalog (adoption) order, one entry per sized use case. */
export function orderSelection(selected: UseCaseSelection[]): UseCaseSelection[] {
  const byId = new Map(selected.map((s) => [s.id, s]));
  return SIZED_USE_CASES.filter((u) => byId.has(u.id)).map((u) => byId.get(u.id) as UseCaseSelection);
}

/** Compute every selected use case and the rollup. */
export function computeCalculator(input: CalculatorInput): CalculatorResult {
  const { deal } = input;
  const useCases: UseCaseResult[] = [];
  const lines: RollupLine[] = [];
  const missing = new Set<string>();
  for (const sel of orderSelection(input.selected)) {
    const uc = getSizedUseCase(sel.id) as SizedUseCase;
    const r = computeUseCase(sel, deal, { tools: input.tools, customTools: input.customTools });
    if (!r) continue;
    useCases.push(r);
    r.missingInputs.forEach((m) => missing.add(m));
    const realizationPct = resolveFactor(sel.realization, uc.realization.pct);
    const attributionPct = resolveFactor(sel.attribution, uc.attribution.pct);
    const programValue = (r.value * realizationPct) / 100;
    lines.push({
      id: uc.id,
      value: r.value,
      realizationPct,
      programValue,
      attributionPct,
      attributedValue: (programValue * attributionPct) / 100,
      pipeline: r.pipeline,
    });
  }
  const sum = (f: (l: RollupLine) => number) => lines.reduce((t, l) => t + f(l), 0);
  const attributed = sum((l) => l.attributedValue);
  const estimate = Number(deal.contractEstimate);
  return {
    useCases,
    rollup: {
      lines,
      gross: sum((l) => l.value),
      program: sum((l) => l.programValue),
      attributed,
      pipelineGross: sum((l) => l.pipeline),
      pipeline: sum((l) => (l.pipeline * l.realizationPct) / 100),
      roi: Number.isFinite(estimate) && estimate > 0 ? attributed / estimate : null,
      missingInputs: [...missing],
    },
  };
}

/* ------------------------------------------------------------------------ UI helpers */

/** Use cases in the selection that need the deal's current annual pipeline (story 29). */
export function casesRequiringCurrentPipeline(ids: readonly string[]): string[] {
  return ids.filter((id) => getSizedUseCase(id)?.requiresCurrentAnnualPipeline);
}

export function requiresCurrentAnnualPipeline(ids: readonly string[]): boolean {
  return casesRequiringCurrentPipeline(ids).length > 0;
}

export interface OverlapNote {
  between: [string, string];
  note: string;
}

/** One note per declared overlapping pair that is fully inside the selection (story 40). */
export function overlapNotes(ids: readonly string[]): OverlapNote[] {
  const selected = new Set(ids);
  const seen = new Set<string>();
  const out: OverlapNote[] = [];
  for (const uc of SIZED_USE_CASES) {
    if (!selected.has(uc.id)) continue;
    for (const o of uc.overlaps) {
      if (!selected.has(o.with)) continue;
      const pair = [uc.id, o.with].sort() as [string, string];
      const k = pair.join('|');
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ between: pair, note: o.note });
    }
  }
  return out;
}

/** The overlap notes that apply to one use case's page / review row. */
export function overlapNotesFor(id: string, ids: readonly string[]): OverlapNote[] {
  return overlapNotes(ids).filter((o) => o.between.includes(id));
}

/** How many of a use case's asked inputs are still on Clay defaults (the review "Assumed" count). */
export function countInputsOnDefaults(
  id: string,
  answers: UseCaseAnswers | null | undefined,
  recordLevels?: RecordLevel[] | null,
  toolInput?: { tools?: Record<string, number | ''> | null; customTools?: CustomTool[] | null },
  methods?: readonly string[] | null,
): { assumed: number; total: number } {
  const uc = getSizedUseCase(id);
  if (!uc) return { assumed: 0, total: 0 };
  const qs = questionsFor(uc, enabledLevels(uc, recordLevels), enabledMethods(uc, methods));
  const assumed = qs.filter((q) => (answers ?? {})[q.key] === undefined).length;
  if (uc.id === 'uc-tool-consolidation') {
    const untouched = toolInput?.tools == null && !(toolInput?.customTools ?? []).length;
    return { assumed: untouched ? 1 : 0, total: 1 };
  }
  return { assumed, total: qs.length };
}

/** Sub-total lookup across a result's sections (summed over record levels). */
export function subTotalValue(result: UseCaseResult, key: string, level?: RecordLevel): number {
  return result.sections
    .filter((s) => level === undefined || s.level === level)
    .reduce((t, s) => t + (s.subTotals.find((x) => x.key === key)?.value ?? 0), 0);
}

